import React from "react";
import {
  Button,
  Badge,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import CartItemsList from "./CartItemsList";
import { buildOrderPayload } from "../utils/orderPayloadBuilder";
import PublicOrderingServices from "../services/PublicOrderingServices";
import { useDispatch } from "react-redux";
import { clearCart } from "../../../slices/cart/publicOrderingCartSlice";
import NumberWithCommas from "../../../pages/Pages/Starter/NumberWithCommas";

// ============= CART SUMMARY OFFCANVAS COMPONENT =============
const CartSummary = ({
  cart,
  onClear,
  onOrderNow,
  orgInfo,
  orgData,
  isOpen,
  toggle,
}) => {
  // support both old and new shape
  const totalItems = cart.reduce(
    (sum, item) => sum + (item.qty || item.quantity || 0),
    0
  );
  const totalPrice = cart.reduce(
    (sum, item) =>
      sum +
      (item.totalPrice || item.basePrice || item.price || 0) *
        (item.qty || item.quantity || 0),
    0
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const orgCode = orgInfo?.orgCode;

  return (
    <Offcanvas
      isOpen={isOpen}
      toggle={toggle}
      direction="bottom"
      style={{
        display: "flex",
        flexDirection: "column",
        padding: "16px",
        height: "80vh",
        borderTopLeftRadius: "20px",
        borderTopRightRadius: "20px",
        maxWidth: "1200px",
        margin: "0 auto",
        left: 0,
        right: 0,
      }}
    >
      <OffcanvasHeader
        toggle={toggle}
        style={{
          borderBottom: "1px solid #e9ecef",
          paddingBottom: "12px",
        }}
      >
        <div className="d-flex align-items-center gap-2 w-100">
          <i
            className="bx bx-cart"
            style={{ fontSize: "24px", color: "#ff7900" }}
          />
          <h5
            className="mb-0 fw-bold"
            style={{ color: "#2c3e50", fontSize: "18px" }}
          >
            Your Order
          </h5>
          <Badge
            color="danger"
            pill
            style={{
              fontSize: "11px",
              padding: "4px 10px",
              backgroundColor: "#ff7900",
              fontWeight: "700",
            }}
          >
            {totalItems === 1 ? `${totalItems} item` : `${totalItems} items`}
          </Badge>{" "}
          <Badge
            color="danger"
            pill
            style={{
              fontSize: "11px",
              padding: "4px 10px",
              backgroundColor: "#ff7900",
              fontWeight: "700",
            }}
          >
            Rs. <NumberWithCommas number={totalPrice} />
          </Badge>
          <div style={{ marginLeft: "auto" }}>
            <Button
              color="link"
              size="sm"
              onClick={onClear}
              className="text-danger text-decoration-none p-0"
              style={{ fontWeight: "700", fontSize: "14px" }}
            >
              <i className="bx bx-trash" style={{ fontSize: "16px" }} />
            </Button>
          </div>
        </div>
      </OffcanvasHeader>

      <OffcanvasBody
        style={{
          display: "flex",
          flexDirection: "column",
          padding: "16px",
        }}
      >
        {/* Cart Items */}
        <div style={{ flex: 1, overflowY: "auto", marginBottom: "16px" }}>
          <CartItemsList cart={cart} />
        </div>

        {/* Divider */}
        <div
          style={{
            height: "1px",
            backgroundColor: "#f0f0f0",
            margin: "8px 0",
          }}
        />

        {/* Total and Action */}
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <div
            style={{
              flex: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              backgroundColor: "#fff",
              borderRadius: "12px",
              padding: "14px 16px",
              border: "2px solid #ff7900",
            }}
          >
            <span
              className="fw-bold"
              style={{ fontSize: "14px", color: "#2c3e50" }}
            >
              Total:
            </span>
            <span
              className="fw-bold"
              style={{ fontSize: "20px", color: "#ff7900" }}
            >
              Rs. <NumberWithCommas number={totalPrice} />
            </span>
          </div>
          <Button
            color="danger"
            style={{
              borderRadius: "12px",
              padding: "14px 28px",
              fontWeight: "700",
              fontSize: "14px",
              whiteSpace: "nowrap",
              backgroundColor: "#ff7900",
              border: "none",
              boxShadow: "0 4px 12px rgba(255, 121, 0, 0.3)",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#e56a00";
              e.currentTarget.style.boxShadow =
                "0 6px 16px rgba(255, 121, 0, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#ff7900";
              e.currentTarget.style.boxShadow =
                "0 4px 12px rgba(255, 121, 0, 0.3)";
            }}
            onClick={async () => {
              const customerInfo = JSON.parse(
                localStorage.getItem("CustomerInfo")
              );
              const isLoggedIn = customerInfo?.accessToken;
              const orderPayload = buildOrderPayload(cart);

              if (!isLoggedIn) {
                const pending = { orderPayload, orgData };
                try {
                  localStorage.setItem("pendingCheckout", JSON.stringify(pending));
                } catch (e) {
                  console.error("Failed to save pending checkout:", e);
                }
                navigate(`/${orgCode}/customer-login`);
                return;
              }

              try {
                // 1. Build API payload
                // orderPayload already built above

                // 2. Call API
                const response = await PublicOrderingServices.orderOnline({
                  token: customerInfo.accessToken,
                  apikey: orgCode,
                  orderPayload: orderPayload,
                });

                console.log("ORDER SUCCESS:", response);

                // 3. Clear cart after successful order
                localStorage.removeItem("publicCartV2");
                console.log("Cart cleared successfully");
                dispatch(clearCart());

                // 4. Navigate to order summary page
                navigate(`/${orgCode}/order-summary`, {
                  state: {
                    orgData,
                    orderPost: response,
                    orderResponse: response,
                  },
                });
              } catch (error) {
                console.error("ORDER FAILED:", error);
                alert(error?.message || "Failed to place order");
              }
            }}
          >
            <i
              className="bx bx-check-circle me-1"
              style={{ fontSize: "16px" }}
            />
            Order Now
          </Button>
        </div>
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default CartSummary;
